import Vue from 'vue'
import { GrowthBook } from '@growthbook/growthbook'
import App from './App.vue'
import router from './router'
import store from './store/index'
import VeeValidate from 'vee-validate'
import CustomRules from './validation/Rules'
import ValidationMessages from './validation/Messages'
import { i18n } from './setup/i18nSetup'
import { getTopbar } from './setup/topbar'
import { defineCustomElement as definePnButton } from '@postnord/web-components/components/pn-button'
import { defineCustomElement as definePnCheckbox } from '@postnord/web-components/components/pn-checkbox'
import { defineCustomElement as definePnDatePicker } from '@postnord/web-components/components/pn-date-picker'
import { defineCustomElement as definePnFooter } from '@postnord/web-components/components/pn-footer'
import { defineCustomElement as definePnIcon } from '@postnord/web-components/components/pn-icon'
import { defineCustomElement as definePnInput } from '@postnord/web-components/components/pn-input'
import { defineCustomElement as definePnModal } from '@postnord/web-components/components/pn-modal'
import { defineCustomElement as definePnOption } from '@postnord/web-components/components/pn-option'
import { defineCustomElement as definePnProgressBar } from '@postnord/web-components/components/pn-progress-bar'
import { defineCustomElement as definePnProgressIndicator } from '@postnord/web-components/components/pn-progress-indicator'
import { defineCustomElement as definePnProgressIndicatorStep } from '@postnord/web-components/components/pn-progress-indicator-step'
import { defineCustomElement as definePnRadioButton } from '@postnord/web-components/components/pn-radio-button'
import { defineCustomElement as definePnSegment } from '@postnord/web-components/components/pn-segment'
import { defineCustomElement as definePnSegmentedControl } from '@postnord/web-components/components/pn-segmented-control'
import { defineCustomElement as definePnSelect } from '@postnord/web-components/components/pn-select'
import { defineCustomElement as definePnSpinner } from '@postnord/web-components/components/pn-spinner'
import { defineCustomElement as definePnTab } from '@postnord/web-components/components/pn-tab'
import { defineCustomElement as definePnTable } from '@postnord/web-components/components/pn-table'
import { defineCustomElement as definePnTablist } from '@postnord/web-components/components/pn-tablist'
import { defineCustomElement as definePnToast } from '@postnord/web-components/components/pn-toast'
import { defineCustomElement as definePnTooltip } from '@postnord/web-components/components/pn-tooltip'
import '@/filters'

// import './registerServiceWorker'  // We'll wait with this.

const gbInstance = Vue.observable(
  new GrowthBook({
    apiHost: process.env.VUE_APP_GROWTHBOOK_API_HOST,
    clientKey: process.env.VUE_APP_GROWTHBOOK_CLIENT_KEY,
    attributes: {
      // Add user attributes here
    },
    enableDevMode: process.env.VUE_APP_GROWTHBOOK_DEV_MODE
  })
)

Vue.prototype.$gb = gbInstance
store.commit('growthbook/SET_GROWTHBOOK_INSTANCE', gbInstance)

Vue.config.productionTip = false

Vue.config.ignoredElements = [
  // `RegExp` to ignore all custom elements that start with "pn-"
  // This is needed for the holistic components
  /^pn-/
]

const registerElement = (tagName, defineElementFn) => {
  if (!customElements.get(tagName)) {
    defineElementFn()
  }
}

registerElement('pn-button', definePnButton)
registerElement('pn-checkbox', definePnCheckbox)
registerElement('pn-date-picker', definePnDatePicker)
registerElement('pn-footer', definePnFooter)
registerElement('pn-icon', definePnIcon)
registerElement('pn-input', definePnInput)
registerElement('pn-modal', definePnModal)
registerElement('pn-option', definePnOption)
registerElement('pn-progress-bar', definePnProgressBar)
registerElement('pn-progress-indicator', definePnProgressIndicator)
registerElement('pn-progress-indicator-step', definePnProgressIndicatorStep)
registerElement('pn-radio-button', definePnRadioButton)
registerElement('pn-segment', definePnSegment)
registerElement('pn-segmented-control', definePnSegmentedControl)
registerElement('pn-select', definePnSelect)
registerElement('pn-spinner', definePnSpinner)
registerElement('pn-tab', definePnTab)
registerElement('pn-table', definePnTable)
registerElement('pn-tablist', definePnTablist)
registerElement('pn-toast', definePnToast)
registerElement('pn-tooltip', definePnTooltip)

if (process.env.NODE_ENV !== 'production') {
  const pnTagsToCheck = [
    'pn-side-menu',
    'pn-button-container',
    'pn-input-container',
    'pn-checkbox-container',
    'pn-radio-controls',
    'pn-radio',
    'pn-toaster',
    'pn-radio-button',
    'pn-toast'
  ]
  const availability = pnTagsToCheck.reduce((acc, tag) => {
    acc[tag] = !!customElements.get(tag)
    return acc
  }, {})
  console.info('[W2P] PN component availability', availability)
}

// Validate all fields on blur and input events
Vue.use(VeeValidate, {
  events: 'blur|input'
})

// Register custom rules and validation messages
CustomRules.register()
VeeValidate.Validator.localize('en', ValidationMessages)

getTopbar()
  .then(result => {
    // Load GrowthBook features before mounting the app to ensure
    // feature flags are available in initial computed properties
    return gbInstance.loadFeatures()
  })
  .then(() => {
    gbInstance.init({ streaming: false }).catch(err => {
      console.error('Error initializing GrowthBook:', err)
    })

    new Vue({
      i18n,
      router,
      store,
      render: h => h(App)
    }).$mount('#app')
  })
  .catch(err => {
    // Fallback: mount app even if topbar or features fail to load
    console.error('Error during app initialization:', err)
    new Vue({
      i18n,
      router,
      store,
      render: h => h(App)
    }).$mount('#app')
  })
